import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Event as EventIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  WhatsApp as WhatsAppIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  CartesianGrid,
  LineChart,
  Line,
  Pie,
  PieChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
} from 'recharts';

import { useAuth } from '../../hooks/useAuth';
import StatsCard from '../../components/dashboard/StatsCard';
import RecentAppointments from '../../components/dashboard/RecentAppointments';
import reporteService from '../../services/reporteService';

const Dashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();

  // Función helper para obtener el rol correctamente (igual que en Sidebar)
  const getUserRole = () => {
    if (!user) return null;
    
    // Si user.rol es un objeto, acceder a la propiedad nombre
    if (typeof user.rol === 'object' && user.rol !== null) {
      return user.rol.nombre || user.rol.name || user.rol.tipo || null;
    }
    
    // Si es string, devolverlo directamente
    if (typeof user.rol === 'string') {
      return user.rol;
    }
    
    return null;
  };

  const [stats, setStats] = useState({
    totalPacientes: 0,
    pacientesNuevosMes: 0,
    totalCitas: 0,
    citasHoy: 0,
    totalHistorias: 0,
    totalFormularios: 0,
  });

  const [citasProximas, setCitasProximas] = useState([]);
  const [pacientesPorEspecialidad, setPacientesPorEspecialidad] = useState([]);
  const [citasPorMes, setCitasPorMes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userRole = getUserRole();
    
    // Solo cargar datos del dashboard si es administrador
    if (userRole === 'Administrador') {
      const fetchDashboardData = async () => {
        try {
          setLoading(true);

          const statsResponse = await reporteService.getStats();
          const citasResponse = await reporteService.obtenerProximasCitas();
          const especialidadesResponse = await reporteService.getPacientesPorEspecialidad();
          const citasMensualResponse = await reporteService.getCitasPorMes();

          setStats(statsResponse.data || {});
          
          let citasAMostrar = [];
          
          if (citasResponse && citasResponse.citas && Array.isArray(citasResponse.citas)) {
            citasAMostrar = citasResponse.citas;
          } else if (Array.isArray(citasResponse)) {
            citasAMostrar = citasResponse;
          } else {
            console.warn('Formato de datos de citas desconocido, usando array vacío');
          }
          
          setCitasProximas(citasAMostrar);
          setPacientesPorEspecialidad(especialidadesResponse.data || []);
          setCitasPorMes(citasMensualResponse.data || []);
        } catch (error) {
          console.error('Error cargando datos del dashboard:', error);
          console.error('Error detallado:', error.response || error.message || error);
        } finally {
          setLoading(false);
        }
      };

      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const COLORS = ['#5D1390', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#5a5c69'];

  // Vista de bienvenida para usuarios no administradores
  const WelcomeView = () => (
    <Box 
      sx={{ 
        flexGrow: 1, 
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}
    >
      <Card 
        sx={{ 
          maxWidth: 600, 
          width: '100%',
          textAlign: 'center',
          p: 4,
          boxShadow: 3
        }}
      >
        <CardContent>
          {/* Logo de la empresa */}
          <Box sx={{ mb: 4 }}>
            <img 
              src={`${process.env.PUBLIC_URL}/assets/logo.png`}
              alt="Neuropsico Logo" 
              style={{ 
                maxWidth: '200px', 
                height: 'auto',
                marginBottom: '20px'
              }} 
            />
          </Box>

          {/* Mensaje de bienvenida */}
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              mb: 3
            }}
          >
            Bienvenid@ al Sistema ERP
          </Typography>

          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ mb: 4 }}
          >
            {user?.nombre || 'Usuario'}
          </Typography>

          {/* Sección de soporte técnico */}
          <Card 
            sx={{ 
              backgroundColor: theme.palette.grey[100],
              p: 3,
              mt: 4
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <HelpIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Soporte Técnico
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              ¿Necesitas ayuda o tienes alguna pregunta?
            </Typography>

            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<WhatsAppIcon />}
              href="https://wa.me/51913413862"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                backgroundColor: '#25D366',
                '&:hover': {
                  backgroundColor: '#128C7E'
                }
              }}
            >
              Contactar por WhatsApp
            </Button>

            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: 2 }}
            >
            
            </Typography>
          </Card>
        </CardContent>
      </Card>
    </Box>
  );

  // Vista del dashboard para administradores
  const AdminDashboard = () => (
    <Box sx={{ flexGrow: 1, py: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dashboard</Typography>
        <Button
          component={RouterLink}
          to="/citas/nueva"
          variant="contained"
          startIcon={<AddIcon />}
        >
          Nueva Cita
        </Button>
      </Box>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Pacientes"
            value={stats.totalPacientes}
            subtitle={`${stats.pacientesNuevosMes} nuevos este mes`}
            icon={<PersonIcon />}
            color="#5D1390"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Citas"
            value={stats.totalCitas}
            subtitle={`${stats.citasHoy} para hoy`}
            icon={<EventIcon />}
            color="#1cc88a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Historias Clínicas"
            value={stats.totalHistorias}
            subtitle="Registros clínicos"
            icon={<TimelineIcon />}
            color="#36b9cc"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Formularios"
            value={stats.totalFormularios}
            subtitle="Formularios de evaluación"
            icon={<PsychologyIcon />}
            color="#f6c23e"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box bgcolor="background.paper" p={2} borderRadius={2} boxShadow={2}>
            <Typography variant="h6" gutterBottom>Citas por mes</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={citasPorMes} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="citas" stroke={theme.palette.primary.main} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box bgcolor="background.paper" p={2} borderRadius={2} boxShadow={2}>
            <Typography variant="h6" gutterBottom>Pacientes por especialidad</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pacientesPorEspecialidad}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {pacientesPorEspecialidad.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <RecentAppointments citas={citasProximas} />
        </Grid>
      </Grid>
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Renderizar vista según el rol del usuario
  const userRole = getUserRole();
  return userRole === 'Administrador' ? <AdminDashboard /> : <WelcomeView />;
};

export default Dashboard;